import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Checkbox,
  CircularProgress,
  Collapse,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { ArtifactItem } from "../../../../../api/storage/storage";
import { analyzeArtifact } from "../../../../../api/registry/registry";
import StorageBrowser, { StorageSelection } from "./StorageBrowser";

// Colour per resource kind a test needs (from manifest.find).
const RESOURCE_COLORS: Record<string, string> = {
  board: "#7B1FA2",
  mobile: "#00838F",
  emulator: "#1565C0",
  hil: "#7B1FA2",
  sil: "#1565C0",
};
const resourceColor = (name: string, targetClass?: string): string => {
  const key = (name || "").toLowerCase();
  if (RESOURCE_COLORS[key]) return RESOURCE_COLORS[key];
  const tc = (targetClass || "").toLowerCase();
  if (tc.includes("mobile")) return "#00838F";
  if (tc.includes("bpmhil") || tc.includes("fpga") || tc.includes("hil")) return "#7B1FA2";
  if (tc.includes("emulator")) return "#1565C0";
  return "#616161";
};

function groupByFramework(tests = []) {
  const result: Record<string, any[]> = {};
  tests.forEach((t: any) => {
    const fw = t.framework || "other";
    (result[fw] ||= []).push(t);
  });
  return result;
}

export default function CasesPicker({ suiteId, onPick }: { suiteId: any; onPick: (v: any) => void }) {
  const [selection, setSelection] = useState<StorageSelection>(null);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [testCases, setTestCases] = useState<any[]>([]);
  const [artifactName, setArtifactName] = useState("");
  const [selectedCases, setSelectedCases] = useState<any[]>([]);
  const [expandedFrameworks, setExpandedFrameworks] = useState<string[]>([]);

  const groupedCases = groupByFramework(testCases);

  useEffect(() => {
    setExpandedFrameworks(Object.keys(groupByFramework(testCases)));
  }, [testCases.length]);

  const handleSelect = (sel: StorageSelection) => {
    setSelection(sel);
    // a new artifact invalidates previous discovery
    setTestCases([]);
    setSelectedCases([]);
    setArtifactName("");
    setAnalyzeError("");
  };

  const handleDiscover = async () => {
    if (!selection) return;
    setAnalyzeError("");
    setLoadingAnalyze(true);
    try {
      const payload: any = { suite_id: suiteId, type: selection.type };
      if (selection.type === "storage") {
        payload.bucket = selection.bucket;
        payload.path = selection.path;
      } else {
        payload.url = selection.url;
      }
      const result = await analyzeArtifact(payload);
      if (result?.tests?.length) {
        setTestCases(result.tests);
        setArtifactName(result.artifact_name || "");
        setSelectedCases(result.tests);
      } else {
        setTestCases([]);
        setAnalyzeError("No test cases found in this artifact.");
      }
    } catch (e) {
      setAnalyzeError("Failed to analyze artifact.");
    } finally {
      setLoadingAnalyze(false);
    }
  };

  const isSelected = (t: any) =>
    !!selectedCases.find(
      (s) => s.path === t.path && s.framework === t.framework && s.test_folder === t.test_folder
    );
  const handleCaseToggle = (test: any) =>
    setSelectedCases((prev) =>
      isSelected(test)
        ? prev.filter(
            (t) =>
              !(t.path === test.path && t.framework === test.framework && t.test_folder === test.test_folder)
          )
        : [...prev, test]
    );
  const handleFrameworkSelectAll = (fw: string) =>
    setSelectedCases((prev) => [...prev, ...groupedCases[fw].filter((t) => !isSelected(t))]);
  const handleFrameworkUnselectAll = (fw: string) =>
    setSelectedCases((prev) => prev.filter((t) => t.framework !== fw));
  const handleExpandCollapse = (fw: string) =>
    setExpandedFrameworks((prev) => (prev.includes(fw) ? prev.filter((e) => e !== fw) : [...prev, fw]));

  const handleContinue = () => {
    if (!testCases.length || !selectedCases.length || !selection) return;
    onPick({
      artifact: {
        type: selection.type,
        bucket: selection.type === "storage" ? selection.bucket : undefined,
        path: selection.type === "storage" ? selection.path : undefined,
        url: selection.type === "url" ? selection.url : undefined,
        artifact_name: artifactName,
      },
      artifact_name: artifactName,
      selected_tests: selectedCases,
    });
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 0.5 }}>Select a test-cases artifact</Typography>
      <Typography color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
        Pick the cases ZIP or folder, then <b>Discover</b> to choose which tests go into the suite.
      </Typography>

      <StorageBrowser
        allowSelect={(item: ArtifactItem) =>
          item.type === "folder" || (item.type === "file" && item.name.endsWith(".zip"))
        }
        enableFolderSelect
        urlPattern={/^https?:\/\/.+\.zip(\?.*)?$/i}
        urlPlaceholder="https://host/bucket/tests.zip"
        urlHint="Direct .zip URL"
        badgeFor={(item) =>
          item.type === "file" && item.name.endsWith(".zip") ? { label: "zip", color: "#e9672d" } : undefined
        }
        onChange={handleSelect}
      />

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={loadingAnalyze ? <CircularProgress size={16} color="inherit" /> : <TravelExploreRoundedIcon />}
          onClick={handleDiscover}
          disabled={loadingAnalyze || !selection}
        >
          {loadingAnalyze ? "Discovering…" : "Discover test cases"}
        </Button>
      </Stack>

      {!!analyzeError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {analyzeError}
        </Alert>
      )}

      {Object.keys(groupedCases).length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 800 }}>
              Discovered tests
            </Typography>
            <Chip
              size="small"
              color="primary"
              label={`${selectedCases.length} / ${testCases.length} selected`}
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          {Object.entries(groupedCases).map(([fw, tests]) => {
            const open = expandedFrameworks.includes(fw);
            const selCount = tests.filter(isSelected).length;
            return (
              <Box
                key={fw}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 1.5, overflow: "hidden" }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => handleExpandCollapse(fw)}
                  sx={{ bgcolor: "background.level1", px: 1.5, py: 1, cursor: "pointer", borderBottom: open ? "1px solid" : 0, borderColor: "divider" }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Typography sx={{ fontWeight: 800, letterSpacing: 0.3 }}>{fw.toUpperCase()}</Typography>
                    <Chip size="small" variant="outlined" label={`${selCount}/${tests.length}`} sx={{ height: 20, fontWeight: 700 }} />
                  </Stack>
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Button size="small" onClick={() => handleFrameworkSelectAll(fw)} sx={{ textTransform: "none" }}>
                      Select all
                    </Button>
                    <Button size="small" color="inherit" onClick={() => handleFrameworkUnselectAll(fw)} sx={{ textTransform: "none" }}>
                      Clear
                    </Button>
                  </Box>
                </Stack>
                <Collapse in={open}>
                  <List dense sx={{ maxHeight: 260, overflowY: "auto", p: 0 }}>
                    {[...tests]
                      .sort((a, b) =>
                        (a.test_folder || "").localeCompare(b.test_folder || "", undefined, { numeric: true })
                      )
                      .map((t) => {
                        const find = (t.manifest?.find || {}) as Record<string, any>;
                        const needs = Object.keys(find);
                        const title = t.manifest?.title;
                        const comment = t.manifest?.comment;
                        const desc =
                          comment && comment !== title && !/^Autogenerated/i.test(comment)
                            ? comment
                            : title && title !== t.test_folder
                            ? title
                            : "";
                        return (
                          <ListItem
                            key={t.path + t.test_folder + fw}
                            disablePadding
                            secondaryAction={
                              <Checkbox edge="end" checked={isSelected(t)} onChange={() => handleCaseToggle(t)} />
                            }
                          >
                            <ListItemButton onClick={() => handleCaseToggle(t)} selected={isSelected(t)}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, py: 0.25, minWidth: 0, pr: 4 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography variant="body2" fontWeight={700}>
                                    {t.test_folder}
                                  </Typography>
                                  {needs.length ? (
                                    needs.map((n) => (
                                      <Chip
                                        key={n}
                                        size="small"
                                        label={n}
                                        sx={{ height: 20, fontWeight: 700, letterSpacing: 0.3, color: "#fff", bgcolor: resourceColor(n, find[n]?.target_class) }}
                                      />
                                    ))
                                  ) : (
                                    <Chip size="small" label="no resources" variant="outlined" sx={{ height: 20 }} />
                                  )}
                                </Stack>
                                {desc && (
                                  <Typography variant="caption" color="text.secondary" noWrap title={desc}>
                                    {desc}
                                  </Typography>
                                )}
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                  </List>
                </Collapse>
              </Box>
            );
          })}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={!selectedCases.length}
              onClick={handleContinue}
            >
              Continue ({selectedCases.length})
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
